import React, { FunctionComponent, useCallback, useContext, useEffect, useState } from "react"
import { Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from "react-router-dom"
import Spinner from "../components/Spinner"
import InfoSection from "../pages/billing/sections/InfoSection"
import ProcessingSection from "../pages/billing/sections/ProcessingSection"
import ContactPage from "../pages/contact/ContactPage"
import Redirect from "../pages/redirect/Redirect"
import { useAppHandlers } from "../services/actions/app/useAppHandlers"
import { useAuthHandlers } from "../services/actions/auth/useAuthHandlers"
import { useAppStore } from "../stores/application/useAppStore"
import { useAuthStore } from "../stores/auth/useAuthStore"
import { BillingStoreProvider } from "../stores/billing/useBillingStore"
import { useConnectionsHandlers } from "../services/actions/connections/useConnectionsHandlers"
import { usePostsHandlers } from "../services/actions/posts/usePostsHandlers"
import { useHubspotHandlers } from "../services/actions/hubspot/useHubspotHandlers"
import Invite from "../pages/Invite/Invite"
import OnboardingPage from "../pages/onboarding/OnboardingPage"
import TeamSection from "../pages/onboarding/sections/TeamSection"
import SurveySection from "../pages/onboarding/sections/SurveySection"
import AllSetSection from "../pages/onboarding/sections/AllSetSection"
import ExtensionInstallSection from "../pages/onboarding/sections/ExtensionInstallSection"
import UnsupportedBrowserSection from "../pages/onboarding/sections/UnsupportedBrowserSection"
import MobileFinishOnLaptopSection from "../pages/onboarding/sections/MobileFinishOnLaptopSection"
import LinkedInSection from "../pages/onboarding/sections/LinkedInSection"
import SyncNetworkSection from "../pages/onboarding/sections/SyncNetworkSection"
import { getOnboardingFirstStepRoute } from "../pages/onboarding/onboardingBrowserUtils"
import { isMobileDevice } from "../utils"
import { AppRoutes } from "../interfaces/store/appStore"
import { shouldOpenNewInbox } from "../services/inbox/inboxNavAccess"
import { hasCampaignsAccess } from "../services/campaigns/campaignsNavAccess"
import { useExtensionRequest } from "../services/apis/extension/useExtensionRequest"
import CanvasPage from "../pages/canvas/CanvasPage"
import HomeSection from "../pages/canvas/sections/HomeSection"
import DashboardSection from "../pages/canvas/sections/DashboardSection"
import RouteGuard from "./RouteGuard"
import ConnectionSection from "../pages/canvas/sections/ConnectionSection"
import SettingsSection from "../pages/canvas/sections/SettingsSection"
import SendingAccountsSection from "../pages/canvas/sections/SendingAccountsSection"
import DataManagement from "../pages/canvas/components/settings/DataManagement"
import LeadDeltaExtension from "../pages/canvas/components/settings/LeadDeltaExtension"
import AccountActivity from "../pages/canvas/components/settings/AccountActivity"
import TagSection from "../pages/canvas/sections/TagSection"
import TaskSection from "../pages/canvas/sections/TaskSection"
import PlansAndPricingSection from "../pages/billing/sections/PlansAndPricingSection"
import NoteSection from "../pages/canvas/sections/NoteSection"
import BillingPage from "../pages/billing/BillingPage"
import ModifyPlanSection from "../pages/billing/sections/ModifyPlanSection"
import CSVPage from "../pages/csv/CSVPage"
import ImportCSV from "../pages/csv/sections/ImportCSV"
import MappingCSV from "../pages/csv/sections/MappingCSV"
import HubSpotPage from "../pages/hubspot/HubSpotPage"
import MappingHubSpot from "../pages/hubspot/sections/MappingHubSpot"
import PaymentSection from "../pages/billing/sections/PaymentSection"
import Checkout from "../pages/billing/sections/Checkout"
import Billing from "../pages/canvas/components/settings/Billing"
import WorkspaceAdmin from "../pages/canvas/components/settings/WorkspaceAdmin"
import TemplatesSection from "../pages/canvas/sections/TemplatesSection"
import InitialLoading from "../components/InitialLoading"
import SignUpSection from "../pages/authentication/sections/SignUpSection"
import SignInSection from "../pages/authentication/sections/SignInSection"
import ForgotPasswordSection from "../pages/authentication/sections/ForgotPasswordSection"
import SSOCallback from "../pages/authentication/sections/SSOCallback"
import DisabledSection from "../pages/canvas/sections/DisabledSection"
import { IntervalProvider } from "./IntervalContext"
import UnderConstruction from "../pages/UnderConstruction"
import IntegrationsSection from "../pages/canvas/sections/IntegrationsSection"
import moment from "moment"
import momentTimezone from "moment-timezone"
import Linkedin from "../pages/canvas/components/settings/Linkedin"
import DeleteUserConfirmation from "../pages/settings/DeleteUserConfirmation"
import CsvEnrichmentSection from "../pages/canvas/sections/CsvEnrichmentSection"
import ProspectsSection from "../pages/canvas/sections/ProspectsSection"
import PostsSection from "../pages/canvas/sections/PostsSection"
// AI Chat is sunset for now (see SideNav); kept commented together with its route below.
// import AiChatSection from "../pages/canvas/sections/AiChatSection"
import CampaignSection from "../pages/canvas/sections/CampaignSection"
import CampaignFormPage from "../pages/canvas/sections/CampaignFormPage"
import CampaignLeadsPage from "../pages/canvas/sections/CampaignLeadsPage"
import InboxSection from "../pages/canvas/sections/InboxSection"
import SharedInboxSection from "../pages/canvas/sections/SharedInboxSection"
import VerifyEmail from "../pages/authentication/sections/VerifyEmail"
import FeedManagement from "../pages/canvas/components/settings/FeedManagement"
import CustomFields from "../pages/canvas/components/settings/CustomFields"
import HubspotFieldMapping from "../pages/canvas/components/settings/HubspotFieldMapping"
import ConversationTags from "../pages/canvas/components/settings/ConversationTagging"
import LinkedInTestPage from "../pages/canvas/sections/LinkedInTestPage"
import { hasRememberedLogin } from "../pages/authentication/lastLoginUserStorage"


interface IAppRouterProps { }

const AppRouter: FunctionComponent<IAppRouterProps> = () => {
    const [searchParams, setSearchParams] = useSearchParams()

    const underConstruction = false

    const { store: { authData, user, workspace, disablePremiumFeatures, scheduledRemoval, trial, subscription, lifetime, isLoggedToLinkedin } } = useAuthStore()
    const { store: { requestLoading, onboardingStep, leadDeltaTabIsActive, extensionInstalled, extensionVersion } } = useAppStore()

    const { setAuthDataHandler, getLoggedInLIUserHandler, getUserHandler, doAutomaticSync, getMeHandler } = useAuthHandlers()
    const { syncExtensionId, checkExtensionStatus, setPaymentConfirmModal } = useAppHandlers()
    const { setConnectionsPageSize, continueConnectionsRemovalHandler } = useConnectionsHandlers()
    const { startPeriodicPostsCheck } = usePostsHandlers()
    const { startHubSpotQueueIntervalHandler } = useHubspotHandlers()

    const [userLoading, setUserLoading] = useState<boolean>(true)
    const [onboardingRoute, setOnboardingRoute] = useState<string>('')
    const { getSessionTokens, setupTemplatesContextMenu } = useExtensionRequest()
    const navigate = useNavigate()
    const location = useLocation();
    const lastItemExist = window.localStorage.getItem('lastSyncAt')

    useEffect(() => {
        getLoggedInLIUserHandler()
        checkExtensionStatus()

		if(leadDeltaTabIsActive && ((user && !user.isSynced) || lastSyncedAtMoreThan12h())){
            doAutomaticSync(user);
        }

        // if trial user just purchased subscription from another tab and returns to this tab, reload updated data
        if(leadDeltaTabIsActive && trial && localStorage.getItem('newSubscriptionStatus') === 'active'){
            localStorage.removeItem('newSubscriptionStatus');
            // reload user to fetch updated subscription data
            getUserHandler();
        }
    }, [leadDeltaTabIsActive])

    const lastSyncedAtMoreThan12h = () => {
        if(lastItemExist && lastItemExist !== 'undefined'){
            const currentDate = moment();
            const syncDate = moment(lastItemExist);
            return (currentDate > syncDate.add(12, 'hours'));
        }
        return false;
    }

    useEffect(() => {
        // is 24 hours passed from last removal
        const lastRemovedAt = momentTimezone.tz(scheduledRemoval?.lastRemovedAt, "America/New_York");
        let nextAllowedTime = lastRemovedAt.clone().set({ hour: 20, minute: 1, second: 0 });
        if (lastRemovedAt.isAfter(nextAllowedTime)) {
            nextAllowedTime = nextAllowedTime.add(1, 'day');
        }
        const currentEasternTime = momentTimezone.tz("America/New_York");
        const isRemovalAllowed = currentEasternTime.isAfter(nextAllowedTime);
        const extensionReady = !!extensionInstalled && !!isLoggedToLinkedin && extensionVersion === process.env.REACT_APP_VERSION;

        // Only resume when extension + LinkedIn are ready; keep schedule alive otherwise.
        if(scheduledRemoval?.inProgress && extensionReady && (scheduledRemoval?.dailyCount < scheduledRemoval?.dailyLimit || isRemovalAllowed)) {
            continueConnectionsRemovalHandler(scheduledRemoval);
        }
      }, [scheduledRemoval, extensionInstalled, isLoggedToLinkedin, extensionVersion])

    // Periodic posts fetch check - starts when user is available
    useEffect(() => {
        console.log('Posts check useEffect triggered - user:', user?._id, 'leadDeltaTabIsActive:', leadDeltaTabIsActive);
        
        let stopPostsCheck: (() => void) | null = null;
        
        if (user) {
            console.log('Starting periodic posts check for user:', user._id);
            stopPostsCheck = startPeriodicPostsCheck();
        } else {
            console.log('Not starting posts check - missing conditions:', { hasUser: !!user });
        }

        return () => {
            if (stopPostsCheck) {
                console.log('Stopping periodic posts check...');
                stopPostsCheck();
            }
        };
    }, [user])

    // HubSpot queue interval - check and start if needed
    // Use useRef to track previous workspace ID to ensure we only run when it actually changes
    const prevWorkspaceIdRef = React.useRef<string | undefined>(undefined);
    
    useEffect(() => {
        const workspaceId = workspace?._id?.toString();
        const prevWorkspaceId = prevWorkspaceIdRef.current;
        
        // Only run if workspace ID actually changed
        if (workspaceId === prevWorkspaceId) {
            return;
        }
        
        // Update ref for next comparison
        prevWorkspaceIdRef.current = workspaceId;
        
        // Only run when workspace ID actually changed
        if (user && workspaceId) {
            startHubSpotQueueIntervalHandler(workspace, user);
        }
    }, [workspace])

    const init = React.useCallback(async () => { // we are leaving this because of the inbox We should introduce the check here for the inbox if the extension exists
        // Get extension tokens
        const tokens: any = await getSessionTokens()

        const {
            accessToken,
            refreshToken
        } = tokens || {};

        // update
        setAuthDataHandler({
            accessToken,
            refreshToken
        }, accessToken !== undefined);
    }, []);

    const isInviteRoute = location.pathname.startsWith(AppRoutes.INVITE);
    const isVerifyEmailRoute = location.pathname === `${AppRoutes.SIGNUP}/verify-email`;

    React.useEffect(() => {
        // Skip init on auth-like public routes — they handle their own session flow.
        if (location.pathname === AppRoutes.SSO_CALLBACK || isInviteRoute || isVerifyEmailRoute) {
            setUserLoading(false);
            return;
        }
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const promoCouponCode = searchParams.get('promoCouponCode')

        if (localStorage.getItem('promoCouponCode')) {
            localStorage.removeItem('promoCouponCode')
        }
        if (typeof promoCouponCode === 'string' && promoCouponCode?.length !== 0) {
            localStorage.setItem('promoCouponCode', JSON.stringify(promoCouponCode))
        }
    }, [])

    useEffect(() => {
        const appID = searchParams.get('appId') ?? undefined;

        syncExtensionId(appID);

        const successModal = searchParams.get('successModal');

        if (successModal) setPaymentConfirmModal();

        if (appID || successModal) setSearchParams(undefined)
    }, [searchParams])


    useEffect(() => {
        if (!('user' in requestLoading)) return;
        const isUserRequestLoading = Boolean(requestLoading['user']);
        const isOnboardingFlow = location.pathname.startsWith(AppRoutes.ONBOARDING) || location.pathname === AppRoutes.PROCESSING || (Boolean(user) && onboardingStep >= 0);
        const onInviteRoute = location.pathname.startsWith(AppRoutes.INVITE);
        const onVerifyEmailRoute = location.pathname === `${AppRoutes.SIGNUP}/verify-email`;
        // Keep onboarding visible while refreshing user after extension install (no full-page spinner).
        setUserLoading(isUserRequestLoading && !(isOnboardingFlow && user) && !onInviteRoute && !onVerifyEmailRoute);
    }, [requestLoading, location.pathname, user, onboardingStep]);

    useEffect(() => {
        if(location.pathname.startsWith(AppRoutes.INVITE)){
            navigate({
                pathname: AppRoutes.INVITE,
                search: location.search,
            });
            return
        }

        // if (
        //    // !verifyEmailRedirected &&
        //     location.pathname.startsWith(`${AppRoutes.SIGNUP}/verify-email`)
        // ) {
        //    // if (location.state?.fromRedirect !== true) {
        //         console.log('VERIFY_EMAIL location.pathname: ' + location.pathname, 'verifyEmailRedirected: ' + verifyEmailRedirected);
        //        return navigate({
        //             pathname: `${AppRoutes.SIGNUP}/verify-email`,
        //             search: location.search,
        //         }, { state: { fromRedirect: true } });
        //    // }
        //   //  return;
        // } else {
        //     console.log('ELSE location.pathname: ' + location.pathname, 'verifyEmailRedirected: ' + verifyEmailRedirected);
        // }

        let onboardingRoute = '';
        switch (onboardingStep) {
            case 0:
                onboardingRoute = AppRoutes.ONBOARDING
                break;
            case -1:
                onboardingRoute = AppRoutes.HOME
                break;
            case 2:
                onboardingRoute = AppRoutes.ONBOARDING_LINKEDIN
                break;
            case 3:
                onboardingRoute = AppRoutes.ONBOARDING_SYNC_NETWORK
                break;
            case 4:
                onboardingRoute = AppRoutes.ONBOARDING_SURVEY
                break;
            case 5:
                onboardingRoute = isMobileDevice() ? AppRoutes.ONBOARDING_MOBILE_FINISH : AppRoutes.ONBOARDING_ALL_SET
                break;
            case 1:
                onboardingRoute = getOnboardingFirstStepRoute()
                break;      
            default:
                onboardingRoute = ''
                break;
        }

        //console.log('onboardingRoute: ' + onboardingRoute, 'location.pathname: ' + location.pathname);

        setOnboardingRoute(onboardingRoute);
        if (user && onboardingRoute.length &&
            location.pathname !== onboardingRoute &&
            location.pathname !== AppRoutes.PROCESSING &&
            location.pathname !== `${AppRoutes.SIGNUP}/verify-email`) {
            const onOnboarding = location.pathname.startsWith(AppRoutes.ONBOARDING);
            const onSignup = location.pathname === AppRoutes.SIGNUP;
            navigate(onOnboarding || onSignup ? onboardingRoute : AppRoutes.PROCESSING, { replace: true });
        }
    }, [user, onboardingStep, location.pathname, navigate])

    useEffect(() => {
        setConnectionsPageSize(user?.preferences?.pageSize ?? 50)
    }, [user?.preferences])

    useEffect(() => {
        if(authData) setupTemplatesContextMenu(authData)
    }, [authData])

    useEffect(() => {
        if(!user?.isSynced && authData && lastItemExist) getMeHandler()
    }, [user])

    //todo: later use spinner when navigating
    const showInitialLoading = userLoading && !isInviteRoute && !isVerifyEmailRoute;
    const campaignsAllowed = hasCampaignsAccess(workspace, trial);
    return (<>{underConstruction ? <UnderConstruction /> : showInitialLoading ? <InitialLoading showInitialLoading={true} /> :
        <Routes>
            {user ? <>
                <Route path={AppRoutes.ONBOARDING} element={<OnboardingPage />}>
                        {/*!workspace && <Route path="" element={<TeamSection />} />*/}
                        <Route path={AppRoutes.ONBOARDING_EXTENSION_INSTALL}  element={<ExtensionInstallSection />} />
                        <Route path={AppRoutes.ONBOARDING_UNSUPPORTED_BROWSER} element={<UnsupportedBrowserSection />} />
                        <Route path={AppRoutes.ONBOARDING_LINKEDIN}  element={<LinkedInSection />} />
                        <Route path={AppRoutes.ONBOARDING_SYNC_NETWORK} element={<SyncNetworkSection />} />
                        <Route path={AppRoutes.ONBOARDING_SURVEY} element={<SurveySection />} />
                        <Route path={AppRoutes.ONBOARDING_MOBILE_FINISH} element={<MobileFinishOnLaptopSection />} />
                        <Route path={AppRoutes.ONBOARDING_ALL_SET} element={<AllSetSection />} />
                </Route>
                {!!onboardingRoute.length && <Route path={AppRoutes.PROCESSING} element={<Navigate to={onboardingRoute} replace />} />}
                <Route element={<IntervalProvider><RouteGuard /></IntervalProvider>}>
                    <Route element={<CanvasPage />}>
                        {disablePremiumFeatures && <Route path={AppRoutes.DISABLED} element={<DisabledSection />} />}
                        {!disablePremiumFeatures && <>
                        <Route path={AppRoutes.HOME} element={<HomeSection />} />
                        <Route path={AppRoutes.DASHBOARD} element={<DashboardSection />} />
                        <Route path={AppRoutes.CONNECTIONS} element={<ConnectionSection />} />
                        <Route path={AppRoutes.TAGS} element={<TagSection />} />
                        <Route path={AppRoutes.TASKS} element={<TaskSection />} />
                        <Route path={AppRoutes.TEMPLATES} element={<TemplatesSection />} />
                        <Route path={AppRoutes.NOTES} element={ <NoteSection />} />
                        <Route path={AppRoutes.POSTS} element={<PostsSection />} />
                        <Route path={AppRoutes.CAMPAIGNS} element={campaignsAllowed ? <CampaignSection /> : <Navigate to={AppRoutes.HOME} />} />
                        <Route path={AppRoutes.CAMPAIGN_NEW} element={campaignsAllowed ? <CampaignFormPage /> : <Navigate to={AppRoutes.HOME} />} />
                        <Route path={AppRoutes.CAMPAIGN_EDIT} element={campaignsAllowed ? <CampaignFormPage /> : <Navigate to={AppRoutes.HOME} />} />
                        <Route path={AppRoutes.CAMPAIGN_LEADS} element={campaignsAllowed ? <CampaignLeadsPage /> : <Navigate to={AppRoutes.HOME} />} />
                        <Route path={AppRoutes.INTEGRATIONS} element={<IntegrationsSection />} />
                        <Route path={`${AppRoutes.INTEGRATIONS}/:integration`} element={<IntegrationsSection />} />
                        <Route path={AppRoutes.CSV_ENRICHMENT} element={trial ? <Navigate to={AppRoutes.HOME} /> : <CsvEnrichmentSection />} />
                        <Route path={AppRoutes.PROSPECTS} element={(workspace?.isBusiness || workspace?.prospectsEnabled) ? <ProspectsSection /> : <Navigate to={AppRoutes.HOME} />} />
                        {/* AI Chat is sunset for now (see SideNav); route kept so it can be brought back.
                        <Route
                            path={AppRoutes.AI_CHAT}
                            element={
                                (workspace?.aiChatEnabled || trial || (workspace?.isBusiness && subscription?.status === 'active'))
                                    ? <AiChatSection />
                                    : <Navigate to={AppRoutes.HOME} />
                            }
                        />
                        */}
                        <Route
                            path={AppRoutes.INBOX}
                            element={
                                shouldOpenNewInbox(workspace, lifetime)
                                    ? <InboxSection />
                                    : <Navigate to={AppRoutes.HOME} replace />
                            }
                        />
                        {/* Proxy accounts exist only where campaigns do — same gate. */}
                        <Route
                            path={AppRoutes.SHARED_INBOX}
                            element={campaignsAllowed && workspace?._id ? <SharedInboxSection /> : <Navigate to={AppRoutes.HOME} replace />}
                        />
                        </>}
                        <Route path={AppRoutes.SETTINGS} element={<SettingsSection />}>
                            <Route path={AppRoutes.SETTINGS_ACCOUNT_ACTIVITY} element={<AccountActivity />} />
                            {!disablePremiumFeatures && <>
                            <Route path={AppRoutes.SETTINGS_DATA_MANAGEMENT} element={<DataManagement />} />
                            <Route path={AppRoutes.SETTINGS_EXTENSION} element={<LeadDeltaExtension />} />
                            <Route path={AppRoutes.SETTINGS_CONVERSATION_TAGS} element={<ConversationTags />} />
                            <Route path={AppRoutes.SETTINGS_LINKEDIN} element={<Linkedin />} />
                            <Route path={AppRoutes.SETTINGS_SENDING_ACCOUNTS} element={<SendingAccountsSection />} />
                            </>}
                            <Route path={AppRoutes.CUSTOM_CONTACT_PROPERTIES} element={<CustomFields />} />
                            <Route path={AppRoutes.SETTINGS_BILLING} element={<Billing />} />
                            <Route path={AppRoutes.SETTINGS_WORKSPACE_ADMIN} element={<WorkspaceAdmin />} />
                            <Route path={AppRoutes.CSV_ENRICHMENT_SETTINGS} element={trial ? <Navigate to={AppRoutes.HOME} /> : <CsvEnrichmentSection />} />
                            <Route path={AppRoutes.SETTINGS_FEED_MANAGEMENT} element={<FeedManagement />} />
                            <Route path={AppRoutes.SETTINGS_HUBSPOT} element={<HubspotFieldMapping />} />
                        </Route>
                        {/* superAdmin-only dev tool — no nav link, access by URL only */}
                        <Route path={AppRoutes.LINKEDIN_TEST} element={<LinkedInTestPage />} />
                    </Route>
                    {!disablePremiumFeatures && <Route element={<CSVPage />}>
                        <Route path={AppRoutes.CSV_IMPORT} element={<ImportCSV />} />
                        <Route path={AppRoutes.CSV_MAPPING} element={<MappingCSV />} />
                    </Route>}
                    <Route element={<HubSpotPage />}>
                        <Route path={AppRoutes.HUBSPOT_MAPPING} element={<MappingHubSpot />} />
                    </Route>
                    <Route path={AppRoutes.BILLING} element={<BillingStoreProvider><BillingPage /></BillingStoreProvider>}>
                        <Route path="" element={<PlansAndPricingSection />} />
                        <Route path={AppRoutes.BILLING_PLAN} element={<ModifyPlanSection />} />
                        <Route element={<Checkout />}>
                            <Route path={AppRoutes.BILLING_PAYMENT} element={<PaymentSection />} />
                            <Route path={AppRoutes.BILLING_PROCESSING} element={<ProcessingSection />} />
                        </Route>
                    </Route>
                </Route>
            </> : <Route>
                <Route path={AppRoutes.SIGNUP} element={<SignUpSection />} />
                <Route path={AppRoutes.SIGNIN} element={<SignInSection />} />
                <Route path={AppRoutes.FORGOT_PASSWORD} element={<ForgotPasswordSection />} />
            </Route>}

            {/* Always reachable — invite verify link must work even if another user is logged in */}
            <Route path={`${AppRoutes.SIGNUP}/verify-email`} element={<VerifyEmail />} />
            <Route path={AppRoutes.INFO} element={<InfoSection />} />
            <Route path={AppRoutes.REDIRECT} element={<Redirect />} />
            <Route path={AppRoutes.INVITE} element={<Invite />} />
            <Route path={AppRoutes.SSO_CALLBACK} element={<SSOCallback />} />
            <Route path={AppRoutes.DELETE_USER_CONFIRMATION} element={<DeleteUserConfirmation />} />
            <Route path={AppRoutes.PRICING} element={<Navigate to={AppRoutes.BILLING} replace />} />

            <Route
                path="*"
                element={
                    disablePremiumFeatures && user
                        ? <Navigate to={AppRoutes.DISABLED} state={{ from: location.pathname }} replace />
                        : <Navigate to={user ? (onboardingStep >= 0 ? AppRoutes.PROCESSING : AppRoutes.HOME) : (hasRememberedLogin() ? AppRoutes.SIGNIN : AppRoutes.SIGNUP)} replace />
                }
            />
        </Routes>}</>)
}

export default AppRouter